import { shiftDateKey, slotMinutesFor, toHHmm, toMinutes, TYPE_COLOR } from "./schedulerUtils";
import type { Channel, ContentItem, ContentType, Program } from "./types";

export const CHANNELS: Channel[] = [
  { id: "nahl", name: "NAHL", logo: "NAHL", logoUrl: "https://images-us-east.htptv.net/htptv/channels/9.png" },
  { id: "na3hl", name: "NA3HL", logo: "3HL", logoUrl: "https://images-us-east.htptv.net/htptv/channels/10.png" },
  { id: "naphl", name: "NAPHL", logo: "PHL", logoUrl: "https://images-us-east.htptv.net/htptv/channels/11.png" },
];

export const LEAGUES = ["NAHL", "NA3HL", "NAPHL"];

/** Data-URI placeholder so thumbnails are real <img> sources without shipping assets. */
function thumbnail(hue: number): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="hsl(${hue},45%,28%)"/>` +
    `<stop offset="1" stop-color="hsl(${(hue + 40) % 360},50%,12%)"/></linearGradient></defs>` +
    `<rect width="320" height="180" fill="url(#g)"/>` +
    `<circle cx="240" cy="60" r="70" fill="hsl(${hue},60%,60%)" fill-opacity="0.12"/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const TITLES: Record<ContentType, string[]> = {
  game: [
    "Austin Bruins vs. Mason City Toros",
    "Bismarck Bobcats vs. Minot Minotauros",
    "St. Cloud Norsemen vs. Fairbanks Ice Dogs",
    "Janesville Jets vs. Kenai River Brown Bears",
    "Yellowstone vs. Bozeman",
    "Utah vs. Northern Colorado",
    "Long Beach vs. Fresno",
    "2021 Championship",
  ],
  show: [
    "Top 10 Plays",
    "Around the NAHL",
    "Coaches Corner",
    "Prospect Watch",
    "The Weekly Rundown",
    "Behind the Bench",
  ],
  highlight: [
    "Goals of the Week",
    "Best Saves of the Month",
    "Big Hits Reel",
    "Shootout Showdown",
    "Overtime Winners",
  ],
  feature: [
    "The Road to the Robertson Cup",
    "Player Profiles: Rising Stars",
    "League Spotlight",
    "Inside the Locker Room",
    "A Season in Review",
  ],
  playlist: [
    "Classic Game Marathon",
    "2019 Championship Run",
    "Best of the Playoffs",
    "Fan Favorites",
    "Rivalry Week",
  ],
  // Uploads come from the admin upload library, never the mock seed.
  upload: [],
  whizzard: [],
};

const DURATION_MIN: Record<ContentType, number[]> = {
  game: [138, 142, 135, 140, 131, 136, 129, 129],
  show: [28, 25, 30, 22, 27, 24],
  highlight: [12, 15, 10, 14, 11],
  feature: [45, 38, 50, 42, 55],
  playlist: [180, 240, 200, 150, 210],
  upload: [],
  whizzard: [],
};

export function buildMockContent(todayKey: string): ContentItem[] {
  let n = 0;
  return (Object.keys(TITLES) as ContentType[]).flatMap((type) =>
    TITLES[type].map((title, i) => {
      n += 1;
      return {
        id: `c-${type}-${i + 1}`,
        title,
        type,
        league: LEAGUES[n % LEAGUES.length],
        thumbnail: thumbnail((n * 47) % 360),
        durationSec: DURATION_MIN[type][i] * 60 + ((n * 13) % 60),
        date: shiftDateKey(todayKey, -(n * 2 + 3)),
      };
    }),
  );
}

interface Slot {
  type: ContentType;
  start: string;
  end: string;
}

/** The daily rotation each channel repeats, mirroring the design reference. */
const DAILY_TEMPLATE: Slot[] = [
  { type: "game", start: "06:00", end: "08:00" },
  { type: "show", start: "08:00", end: "09:00" },
  { type: "game", start: "09:00", end: "12:30" },
  { type: "feature", start: "12:30", end: "14:00" },
  { type: "game", start: "14:00", end: "17:00" },
  { type: "show", start: "17:00", end: "18:00" },
  { type: "playlist", start: "18:00", end: "22:00" },
  { type: "game", start: "22:00", end: "24:00" },
];

/** Seeded programs for a window of days around today. */
export function buildMockPrograms(
  todayKey: string,
  content: ContentItem[],
  daysBack = 7,
  daysAhead = 21,
): Program[] {
  const programs: Program[] = [];
  for (let day = -daysBack; day <= daysAhead; day += 1) {
    const date = shiftDateKey(todayKey, day);
    CHANNELS.forEach((channel, ci) => {
      DAILY_TEMPLATE.forEach((slot, si) => {
        const pool = content.filter((c) => c.type === slot.type);
        const item = pool[Math.abs(day + si + ci * 2) % pool.length];
        const slotSec = (toMinutes(slot.end) - toMinutes(slot.start)) * 60;
        programs.push({
          id: `p-${channel.id}-${date}-${si}`,
          channelId: channel.id,
          contentId: item.id,
          title: item.title,
          description: null,
          type: item.type,
          color: TYPE_COLOR[item.type],
          thumbnail: item.thumbnail,
          durationSec: item.durationSec,
          contentDate: item.date,
          date,
          startTime: slot.start,
          endTime: slot.end,
          trimInSec: 0,
          trimOutSec: Math.min(item.durationSec, slotSec),
        });
      });
    });
  }
  return programs;
}

/** Build a program for freshly placed content. */
export function programFromContent(
  item: ContentItem,
  channelId: string,
  date: string,
  startMin: number,
  id: string,
): Program {
  return {
    id,
    channelId,
    contentId: item.id,
    title: item.title,
    // Venue has no field of its own in the guide API (see
    // ProgramDetailsPanel), so a placed game defaults its description to the
    // venue it actually carries; an admin can still overwrite it via Edit
    // Program.
    description: item.facility ?? null,
    type: item.type,
    color: TYPE_COLOR[item.type],
    thumbnail: item.thumbnail,
    logos: item.logos,
    channelLogo: item.channelLogo,
    durationSec: item.durationSec,
    durationEstimated: item.durationEstimated,
    contentDate: item.date,
    source: item.source,
    date,
    startTime: toHHmm(startMin),
    endTime: toHHmm(startMin + slotMinutesFor(item.durationSec)),
    trimInSec: 0,
    trimOutSec: item.durationSec,
  };
}
